import React, {useEffect, useMemo, useRef, useState} from "react";
import {css, Theme} from "@emotion/react";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {object} from "yup";

import {Button} from "@pg-design/button-module";
import {m} from "@pg-design/helpers-css";
import {borderRadius, gap, mt, p} from "@pg-design/helpers-css-strings";
import {Modal} from "@pg-design/modal-module";
import {FormProviderRHF, InputField, PhoneFieldControlled} from "@pg-design/rhf-components-module";
import {Highlight, Text} from "@pg-design/text-module";
import {hitGtmUserPhoneNumber} from "@pg-mono/google-tag-manager/src/gtm_user_phone_number";
import {useFormRHF} from "@pg-mono/react-hook-forms-utils";

import type {IUserProfile} from "../../types/IUserProfile";
import {useUserUpdatePhoneNumberModalContext} from "../context/UserUpdatePhoneNumberModalContext";

interface IProps {
    isOpen: boolean;
    onSuccess?: () => void;
}

interface IFormData {
    name: string;
    phone: {
        code: string | number;
        number: string;
    };
}

export const UserUpdatePhoneNumberModal = (props: IProps) => {
    const {isOpen, onSuccess} = props;
    const context = useUserUpdatePhoneNumberModalContext();
    const [userInfo, setUserInfo] = useState<IUserProfile | null>(null);

    const formDirtyRef = useRef<boolean>(false);
    const formTouchedRef = useRef<boolean>(false);

    // Fetch user profile on mount and when modal opens
    useEffect(() => {
        if (isOpen) {
            context.getUserProfile().then((profile) => {
                setUserInfo(profile);
            });
        }
    }, [isOpen, context]);

    const validationSchema = object().shape({
        name: Yup.string().required(context.getValidationMessage("required")),
        phone: object().shape({
            code: Yup.mixed<string | number>().required(),
            number: Yup.string()
                .required("Podaj numer telefonu")
                .matches(/^\d+$/, "Podaj poprawny numer telefonu")
                .min(9, "Numer telefonu musi mieć 9 cyfr")
                .max(9, "Numer telefonu musi mieć 9 cyfr")
        })
    });

    useEffect(() => {
        if (isOpen) {
            hitGtmUserPhoneNumber("view");
        }
    }, [isOpen]);

    const countryCodes = context.getCountryCodes();
    const userFirstName = userInfo?.name?.split(" ")[0];
    const phoneParts = userInfo?.phone?.split(" ") ?? [];

    const defaultValues = useMemo<IFormData>(() => {
        return {
            name: userInfo?.name ?? "",
            phone: {
                code: context.defaultCountryCode,
                number: phoneParts[1] ?? ""
            }
        };
    }, [userInfo]);

    const form = useFormRHF<IFormData>({
        resolver: yupResolver(validationSchema),
        mode: "onBlur",
        defaultValues
    });

    // Update form values when userInfo loads (after initial mount)
    useEffect(() => {
        if (userInfo) {
            form.reset(defaultValues);
        }
    }, [userInfo, form, defaultValues]);

    useEffect(() => {
        if (form.formState.isDirty && !formDirtyRef.current) {
            formDirtyRef.current = true;
            hitGtmUserPhoneNumber("fill");
        }
    }, [form.formState.isDirty, context]);

    const onFormClick = () => {
        if (!formTouchedRef.current) {
            formTouchedRef.current = true;
            hitGtmUserPhoneNumber("click");
        }
    };

    const onCloseButton = () => {
        context.onClose();
    };

    const onSubmit = async (values: IFormData) => {
        const response = await context.updateUserProfile({
            name: values.name,
            phone: values.phone ? `${values.phone.code} ${values.phone.number}` : ""
        });

        hitGtmUserPhoneNumber("submit");

        if (response.error) {
            const {error} = response;

            if (error.data) {
                for (const field of Object.keys(error.data)) {
                    const message = error.data[field];

                    form.setError(field as keyof IFormData, {
                        message: Array.isArray(message) ? message.join(", ") : message
                    });
                }
            } else {
                context.onError({message: "Wystąpił błąd podczas aktualizacji danych"});
            }
        } else {
            onSuccess?.();
        }
    };

    if (!userInfo) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} onModalClose={onCloseButton} css={modalStyle}>
            <Modal.CloseButton
                placement="right"
                variant="rounded"
                css={css`
                    ${m(2)}
                `}
            />
            <Modal.Content>
                <Text variant="headline_3" align="left" mb={0.5}>
                    Witaj{" "}
                    {userInfo.name && (
                        <Highlight>
                            <span css={boldTextStyle}>{userFirstName}</span>
                        </Highlight>
                    )}
                </Text>
                <Text variant="body_copy_2" mb={3}>
                    Uzupełnij swoje dane, aby odkryć wszystkie ceny.
                </Text>
                <FormProviderRHF {...form}>
                    <form css={formStyle} onSubmit={form.handleSubmit(onSubmit)}>
                        <div css={formStyle} onClick={onFormClick}>
                            <InputField name="name" placeholder="Imię i nazwisko" disabled={!!userInfo.name} />
                            <PhoneFieldControlled name="phone" codes={countryCodes} placeholder="Telefon" maxMenuHeight={150} />
                        </div>
                        <Button variant="filled_primary" type="submit" fullWidth css={buttonStyle}>
                            POTWIERDŹ DANE
                        </Button>
                    </form>
                </FormProviderRHF>
            </Modal.Content>
        </Modal>
    );
};

const modalStyle = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.sm}) {
        ${p(2)};
        ${borderRadius(2)};
        width: 544px;
    }
`;

const formStyle = css`
    display: flex;
    flex-direction: column;
    ${gap(1)};
`;

const buttonStyle = css`
    ${mt(2)};
`;

const boldTextStyle = css`
    font-weight: 700;
`;
import React, {useEffect, useMemo, useRef, useState} from "react";
import {css, Theme} from "@emotion/react";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {object} from "yup";

import {Button} from "@pg-design/button-module";
import {m} from "@pg-design/helpers-css";
import {borderRadius, gap, mt, p} from "@pg-design/helpers-css-strings";
import {Modal} from "@pg-design/modal-module";
import {FormProviderRHF, InputField, PhoneFieldControlled} from "@pg-design/rhf-components-module";
import {Highlight, Text} from "@pg-design/text-module";
import {hitGtmUserPhoneNumber} from "@pg-mono/google-tag-manager/src/gtm_user_phone_number";
import {useFormRHF} from "@pg-mono/react-hook-forms-utils";

import type {IUserProfile} from "../../types/IUserProfile";
import {useUserUpdatePhoneNumberModalContext} from "../context/UserUpdatePhoneNumberModalContext";

interface IProps {
    isOpen: boolean;
    onSuccess?: () => void;
}

interface IFormData {
    name: string;
    phone: {
        code: string | number;
        number: string;
    };
}

export const UserUpdatePhoneNumberModal = (props: IProps) => {
    const {isOpen, onSuccess} = props;
    const context = useUserUpdatePhoneNumberModalContext();
    const [userInfo, setUserInfo] = useState<IUserProfile | null>(null);

    const formDirtyRef = useRef<boolean>(false);
    const formTouchedRef = useRef<boolean>(false);

    // Fetch user profile on mount and when modal opens
    useEffect(() => {
        if (isOpen) {
            context.getUserProfile().then((profile) => {
                setUserInfo(profile);
            });
        }
    }, [isOpen, context]);

    const validationSchema = object().shape({
        name: Yup.string().required(context.getValidationMessage("required")),
        phone: object().shape({
            code: Yup.mixed<string | number>().required(),
            number: Yup.string()
                .required("Podaj numer telefonu")
                .matches(/^\d+$/, "Podaj poprawny numer telefonu")
                .min(9, "Numer telefonu musi mieć 9 cyfr")
                .max(9, "Numer telefonu musi mieć 9 cyfr")
        })
    });

    useEffect(() => {
        if (isOpen) {
            hitGtmUserPhoneNumber("view");
        }
    }, [isOpen]);

    const countryCodes = context.getCountryCodes();
    const userFirstName = userInfo?.name?.split(" ")[0];
    const phoneParts = userInfo?.phone?.split(" ") ?? [];

    const defaultValues = useMemo<IFormData>(() => {
        return {
            name: userInfo?.name ?? "",
            phone: {
                code: context.defaultCountryCode,
                number: phoneParts[1] ?? ""
            }
        };
    }, [userInfo]);

    const form = useFormRHF<IFormData>({
        resolver: yupResolver(validationSchema),
        mode: "onBlur",
        defaultValues
    });

    // Update form values when userInfo loads (after initial mount)
    useEffect(() => {
        if (userInfo) {
            form.reset(defaultValues);
        }
    }, [userInfo, form, defaultValues]);

    useEffect(() => {
        if (form.formState.isDirty && !formDirtyRef.current) {
            formDirtyRef.current = true;
            hitGtmUserPhoneNumber("fill");
        }
    }, [form.formState.isDirty, context]);

    const onFormClick = () => {
        if (!formTouchedRef.current) {
            formTouchedRef.current = true;
            hitGtmUserPhoneNumber("click");
        }
    };

    const onCloseButton = () => {
        context.onClose();
    };

    const onSubmit = async (values: IFormData) => {
        const response = await context.updateUserProfile({
            name: values.name,
            phone: values.phone ? `${values.phone.code} ${values.phone.number}` : ""
        });

        hitGtmUserPhoneNumber("submit");

        if (response.error) {
            const {error} = response;

            if (error.data) {
                for (const field of Object.keys(error.data)) {
                    const message = error.data[field];

                    form.setError(field as keyof IFormData, {
                        message: Array.isArray(message) ? message.join(", ") : message
                    });
                }
            } else {
                context.onError({message: "Wystąpił błąd podczas aktualizacji danych"});
            }
        } else {
            onSuccess?.();
        }
    };

    if (!userInfo) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} onModalClose={onCloseButton} css={modalStyle}>
            <Modal.CloseButton
                placement="right"
                variant="rounded"
                css={css`
                    ${m(2)}
                `}
            />
            <Modal.Content>
                <Text variant="headline_3" align="left" mb={0.5}>
                    Witaj{" "}
                    {userInfo.name && (
                        <Highlight>
                            <span css={boldTextStyle}>{userFirstName}</span>
                        </Highlight>
                    )}
                </Text>
                <Text variant="body_copy_2" mb={3}>
                    Uzupełnij swoje dane, aby odkryć wszystkie ceny.
                </Text>
                <FormProviderRHF {...form}>
                    <form css={formStyle} onSubmit={form.handleSubmit(onSubmit)}>
                        <div css={formStyle} onClick={onFormClick}>
                            <InputField name="name" placeholder="Imię i nazwisko" disabled={!!userInfo.name} />
                            <PhoneFieldControlled name="phone" codes={countryCodes} placeholder="Telefon" maxMenuHeight={150} />
                        </div>
                        <Button variant="filled_primary" type="submit" fullWidth css={buttonStyle}>
                            POTWIERDŹ DANE
                        </Button>
                    </form>
                </FormProviderRHF>
            </Modal.Content>
        </Modal>
    );
};

const modalStyle = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.sm}) {
        ${p(2)};
        ${borderRadius(2)};
        width: 544px;
    }
`;

const formStyle = css`
    display: flex;
    flex-direction: column;
    ${gap(1)};
`;

const buttonStyle = css`
    ${mt(2)};
`;

const boldTextStyle = css`
    font-weight: 700;
`;
